/**
 * Uma leitura que o @DevOps fez na infra, nos quatro estados da spec
 * (2026-10-09 §5): carregando, ok, "não consegui ler: <motivo> · tentar de
 * novo" e sem permissão. "Não consegui" nunca aparece como ok (D1).
 *
 * Status com ícone de traço E texto (a11y): a cor sozinha não diz nada a quem
 * não a vê. `aria-live` para a leitura que termina enquanto a pessoa está no
 * passo.
 */
import { Anchor, Group, Loader, Text } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck, IconLock } from '@tabler/icons-react'
import axios from 'axios'

export type EstadoDaLeitura = 'carregando' | 'ok' | 'erro' | 'sem_permissao'

/** O estado de uma `useQuery` como leitura: 403 é "sem permissão", nunca "erro". */
export function estadoDaQuery(q: { isLoading: boolean; error: unknown; data?: unknown }): EstadoDaLeitura {
  if (q.isLoading) return 'carregando'
  if (q.error) {
    return axios.isAxiosError(q.error) && q.error.response?.status === 403 ? 'sem_permissao' : 'erro'
  }
  return 'ok'
}

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

interface Props {
  estado: EstadoDaLeitura
  /** O que foi lido, dito como frase ("Bloco 10.25.0.0/16 livre no IPAM"). */
  texto?: string
  motivo?: string
  onTentarDeNovo?: () => void
}

export function LeituraDoDevops({ estado, texto, motivo, onTentarDeNovo }: Props) {
  let corpo: React.ReactNode
  if (estado === 'carregando') {
    corpo = (
      <>
        <Loader size={14} aria-hidden />
        <Text size="xs" c="dimmed" style={BARLOW}>Estou analisando junto com o @DevOps…</Text>
      </>
    )
  } else if (estado === 'ok') {
    corpo = (
      <>
        <IconCircleCheck size={16} stroke={1.5} color="var(--mantine-color-teal-7)" aria-hidden />
        <Text size="xs" style={BARLOW}>{texto}</Text>
      </>
    )
  } else if (estado === 'sem_permissao') {
    corpo = (
      <>
        <IconLock size={16} stroke={1.5} color="var(--mantine-color-dimmed)" aria-hidden />
        <Text size="xs" c="dimmed" style={BARLOW}>
          Sem permissão para esta leitura neste tenant.
        </Text>
      </>
    )
  } else {
    corpo = (
      <>
        <IconAlertTriangle size={16} stroke={1.5} color="var(--mantine-color-yellow-8)" aria-hidden />
        <Text size="xs" style={BARLOW}>
          {`Não consegui ler: ${motivo ?? 'sem resposta'}`}
        </Text>
        {onTentarDeNovo && (
          <Anchor component="button" type="button" size="xs" onClick={onTentarDeNovo}>
            tentar de novo
          </Anchor>
        )}
      </>
    )
  }
  return (
    <Group gap={6} wrap="wrap" align="center" aria-live="polite" data-estado={estado}>
      {corpo}
    </Group>
  )
}
